import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Stack, Typography } from '@mui/material';
import GoogleFontIcon from 'src/icons/GoogleFontIcon';
import { RootState } from 'src/redux/rootReducerType';
import type { Certification } from 'src/types/Certification';
import { fadeStyle } from 'src/components/NewRenterInterface/constants';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';
import { getRequestedDocs, getSignedByGroup } from './signatureFilters';
import RequestedSignaturesSection from './RequestedSignaturesSection';
import SignedDocumentsSection from './SignedDocumentsSection';
import SavedSignatureCard from './SavedSignatureCard';
import SavedInitialsCard from './SavedInitialsCard';

const RenterSignaturePage: FC = () => {
  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );
  const isImpersonated = Boolean(user?.impersonator);

  const certifications = useSelector(
    (state: RootState) => state.newRenterInterface.certifications
  ) as Certification[];

  const userId = user?.id ?? 0;

  const requestedDocs = useMemo(
    () => getRequestedDocs(certifications, userId),
    [certifications, userId]
  );

  const signedGroups = useMemo(
    () => getSignedByGroup(certifications, userId),
    [certifications, userId]
  );

  const hasContent =
    requestedDocs.length > 0 || Object.keys(signedGroups).length > 0;

  return (
    <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh' }}>
      <PageHeader
        title="Signatures"
        icon={<GoogleFontIcon name="ink_pen" />}
        subtitle="Sign your documents electronically"
      />
      <Stack gap={2.5} sx={{ p: 3, ...fadeStyle }}>
        <Stack direction="row" gap={1.5}>
          <SavedSignatureCard isImpersonated={isImpersonated} />
          <SavedInitialsCard isImpersonated={isImpersonated} />
        </Stack>

        {!hasContent && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body1" color="text.secondary">
              No signature requests at this time.
            </Typography>
          </Box>
        )}

        <RequestedSignaturesSection docs={requestedDocs} />
        <SignedDocumentsSection signedGroups={signedGroups} />
      </Stack>
    </Box>
  );
};

export default RenterSignaturePage;
